import moment from "moment"
import {
  DepositCodeZplValues,
  renderDepositCodeZpl,
  resolveDepositCodeZplTemplate,
} from "shared/depositCodePrintData"
import { Reservation } from "shared/reservation"
import { parseSst2Utc } from "src/sst2DateUtils"
import {
  BrowserPrintNoPrinterError,
  getDefaultPrinter,
  writeToPrinter,
} from "./browserPrint"

export type DepositCodeLabelContext = {
  reservation: Reservation
  lockerName?: string
  organizationName?: string
  /** The drawer's door label, used when the reservation lacks its own. */
  doorLabel?: string
  now?: Date
}

export const buildDepositCodeZplValues = ({
  reservation,
  lockerName,
  organizationName,
  doorLabel,
  now = new Date(),
}: DepositCodeLabelContext): DepositCodeZplValues => {
  const sst = reservation?.sstReservation
  // v2 rows hold naive UTC; `parseSst2Utc` reads both shapes (sst2DateUtils).
  const expiration = parseSst2Utc(sst?.expiration_time)
  return {
    depositCode: sst?.delivery_code,
    receptionCode: sst?.reception_code,
    doorLabel: sst?.door?.door_label || doorLabel,
    lockerName,
    reservationNumber: sst?.number,
    expiration: expiration ? moment(expiration).format("YYYY-MM-DD HH:mm") : "",
    organizationName,
    printedAt: moment(now).format("YYYY-MM-DD HH:mm"),
  }
}

export const renderDepositCodeLabel = (
  templateOverride: string | undefined,
  context: DepositCodeLabelContext,
) =>
  renderDepositCodeZpl(
    resolveDepositCodeZplTemplate(templateOverride),
    buildDepositCodeZplValues(context),
  )

/**
 * Sends ZPL to the Browser Print default printer. The printer is re-read on
 * every print rather than taken from the health cache, so a printer changed in
 * the Browser Print app a second ago is the one that prints.
 */
export const sendZplToDefaultPrinter = async (zpl: string) => {
  const printer = await getDefaultPrinter()
  if (!printer) throw new BrowserPrintNoPrinterError()
  await writeToPrinter(printer, zpl)
  return printer
}
